import { decodeJwt } from 'jose';
import { exchangeRefreshToken } from './refreshToken';
import {
  getStoredToken,
  setStoredToken,
  removeStoredToken,
} from './tokenStorage';

function isTokenExpired(token: string): boolean {
  try {
    const decoded = decodeJwt(token);
    if (!decoded.exp) {
      return true;
    }

    // Add a 30-second buffer to account for possible clock skew
    const currentTime = Math.floor(Date.now() / 1000) + 30;
    return decoded.exp < currentTime;
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return true;
  }
}

// Singleton promise to prevent multiple concurrent refresh requests
// This is critical when using refresh token rotation (e.g., Okta)
let refreshPromise: Promise<string | null> | null = null;

async function tryRefreshToken(): Promise<string | null> {
  // If a refresh is already in progress, wait for it
  if (refreshPromise) {
    return refreshPromise;
  }

  // Start a new refresh
  refreshPromise = doRefreshToken();
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

async function doRefreshToken(): Promise<string | null> {
  const tokenData = await exchangeRefreshToken();
  if (tokenData?.access_token) {
    setStoredToken(tokenData.access_token);
    return tokenData.access_token;
  }
  return null;
}

export async function getValidToken(): Promise<string | null> {
  const token = getStoredToken();

  // If no token exists, try to refresh from cookie
  if (!token) {
    return await tryRefreshToken();
  }

  // If token is still valid, return it
  if (!isTokenExpired(token)) {
    return token;
  }

  // Token is expired, remove it and try to refresh
  removeStoredToken();
  return await tryRefreshToken();
}
